import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useHttpGetCountriesOnMount, useHttpGetOrganizationByIdOnMount, useHttpToPatchOrganization } from '../../hooks';
import { Button, ContainerElement, ContainerWithSidebar, IMenuItem, MixerLoader } from '../../components/Common';
import { AuthState } from '../../store/slices/auth.slice';
import { CurrencyDto, CustomField } from '../../commonTypes/organization.types';
import { useFormik } from 'formik';
import MainSettings from '../Organization';
import { useParams } from 'react-router';
import useHttpGetCurrencies from '../../hooks/organization/organizationApi/useHttpGetCurrencies';
import { CountryDto } from '../../commonTypes/country.types';
import { ORGANIZATION_SETTINGS_SCHEMA } from '../Organization';
import { shallowCompare } from '../../util/general';
import AdditionalSettings from '../Organization/AdditionalSettings';
import CustomComponent from '../Organization/CustomComponent';
import { selectOrganization } from '../../store/slices/organization.slice';
import { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import TransferOwnership from '../Organization/TransferOwnership';

export interface ISettingsValues {
    logoUrl?: string;
    name: string;
    countryId: string;
    city?: string;
    streetline1?: string;
    streetline2?: string;
    contact?: string;
    email?: string;
    currencyId?: string;
    website?: string;
    industry?: string;
    numberOfEmployees?: string;
    businessType?: string;
    customFields?: CustomField[];
    taxNumber?: string;
    registrationNumber?: string;
    taxNumberLabel: string;
    registrationNumberLabel: string;
}

interface ISettingsProps {
    countries: {
        label: string;
        value: string | number;
    }[];
    currencies: {
        label: string;
        value: string | number;
    }[];
    fullCurrencies: CurrencyDto[];
}

const Settings = ({ countries, currencies, fullCurrencies }: ISettingsProps) => {
    const { t } = useTranslation();
    const auth = useSelector<RootState, AuthState>(state => state.auth);

    const organizationId = auth.value.organizationId;
    const organization = useSelector(selectOrganization);
    const [isEditCustomComponentModalOpen, setIsEditCustomComponentModalOpen] = useState(false);

    const { exec: updateOrganization } = useHttpToPatchOrganization();

    const findCurrency = (currencyId: string | undefined) => {
        const currency = fullCurrencies.find(curr => curr.id === currencyId);
        return currency;
    };

    const handleSubmit = (values: ISettingsValues) => {
        updateOrganization(organizationId, {
            name: values.name,
            website: values.website,
            phone: values.contact,
            address: {
                countryId: values.countryId,
                city: values.city,
                streetline1: values.streetline1,
                streetline2: values.streetline2,
            },
            contactEmail: values.email,
            contactPhone: values.contact,
            industry: values.industry,
            numOfEmployees: values.numberOfEmployees,
            businessType: values.businessType,
            configuration: {
                customFields: values.customFields ?? [],
            },
            currency: findCurrency(values.currencyId),
            taxNumber: values.taxNumber,
            registrationNumber: values.registrationNumber,
            taxNumberLabel: values.taxNumberLabel,
            registrationNumberLabel: values.registrationNumberLabel,
        });
    };

    const getInitialValues = (): ISettingsValues => {
        return {
            logoUrl: organization.logoUrl ?? '',
            name: organization.name ?? '',
            countryId: organization.address?.countryId?.toString() ?? '',
            city: organization.address?.city ?? '',
            streetline1: organization.address?.streetline1 ?? '',
            streetline2: organization.address?.streetline2 ?? '',
            contact: organization.contactPhone ?? '',
            email: organization.contactEmail ?? '',
            currencyId: organization.currency?.id ?? '',
            website: organization.website ?? '',
            industry: organization.industry ?? '',
            numberOfEmployees: organization.numOfEmployees ?? '',
            businessType: organization.businessType ?? '',
            customFields: organization.configuration.customFields ?? [],
            taxNumber: organization.taxNumber ?? '',
            registrationNumber: organization.registrationNumber ?? '',
            taxNumberLabel: organization.taxNumberLabel ?? '',
            registrationNumberLabel: organization.registrationNumberLabel ?? '',
        };
    };

    // TODO: Improve invalid form detection (e.g., highlight fields or disable submit button when form is invalid)
    const context = useFormik<ISettingsValues>({
        initialValues: getInitialValues(),
        validationSchema: ORGANIZATION_SETTINGS_SCHEMA(t),
        onSubmit: () => {
            handleSubmit(context.values);
        },
    });

    const initialValuesUnchanged = () => {
        return shallowCompare(context.values, context.initialValues);
    };

    const elements: ContainerElement[] = [
        {
            id: 'mainSettings',
            title: t('settings.mainSettings'),
            element: <MainSettings context={context} countries={countries} currencies={currencies} />,
        },
        {
            id: 'additionalSettings',
            title: 'Additional Settings',
            element: <AdditionalSettings context={context} />,
        },
        {
            id: 'customComponent',
            title: (
                <div className="custom-title">
                    {t('settings.custom')}
                    <button type="button" onClick={() => setIsEditCustomComponentModalOpen(prevState => !prevState)}>
                        <img src="/img/common/pen.svg" alt="" />
                    </button>
                </div>
            ),
            element: (
                <CustomComponent
                    context={context}
                    openEditCustomFiledsModal={isEditCustomComponentModalOpen}
                    setIsEditCustomComponentModalOpen={setIsEditCustomComponentModalOpen}
                />
            ),
        },
        {
            id: 'Buttons',
            title: '',
            element: (
                <div className="create-policy-buttons">
                    <Button
                        className="button-style"
                        variant="text"
                        text={t('common.cancel')}
                        color="blue"
                        onClick={() => context.resetForm({ values: context.initialValues })}
                    />
                    <Button
                        className="button-style"
                        variant="contained"
                        text={t('common.saveChanges')}
                        isSubmit
                        disabled={initialValuesUnchanged()}
                    />
                </div>
            ),
        },
        {
            id: 'dangerZone',
            title: <div className="danger-zone__title">{t('settings.dangerZone')}</div>,
            element: (
                <div className="danger-zone">
                    <TransferOwnership />
                </div>
            ),
        },
    ];

    return (
        <form onSubmit={context.handleSubmit}>
            <ContainerWithSidebar
                title={t('settings.orgSettings')}
                elements={elements}
                className="container-with-sidebar__content-wrapper-settings"
            />
        </form>
    );
};

const SettingsSafetyWrapper = () => {
    const auth = useSelector<RootState, AuthState>(state => state.auth);
    const organizationId = auth.value.organizationId;
    const { id } = useParams();
    const { areCountriesReady, data: countries } = useHttpGetCountriesOnMount();
    const { data: currencies, isReady: areCurrenciesReady } = useHttpGetCurrencies();
    const isAdd = !id;
    const { isOrganizationReady } = useHttpGetOrganizationByIdOnMount(organizationId);

    if (!areCountriesReady || (!isAdd && !isOrganizationReady) || !areCurrenciesReady) return <MixerLoader position={'centerOnParent'} />;
    const mappedCountries: IMenuItem[] = countries.map((country: CountryDto) => ({ label: country.name, value: country.id }));
    const mappedCurrencies: IMenuItem[] = currencies.map((currency: CurrencyDto) => ({ label: currency.name, value: currency.id }));

    if (!isOrganizationReady) return <MixerLoader position="centerOnParent" />;
    return <Settings countries={mappedCountries} currencies={mappedCurrencies} fullCurrencies={currencies} />;
};

export default SettingsSafetyWrapper;
